'use client';

import React, { useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Combobox } from '@headlessui/react';
import Fuse from 'fuse.js';

type ProductType = {
    id: number;
    title: string;
    slug: string;
    productCategoryId?: number;
    productCategories?: {
        slug: string;
        title?: string;
    };
};

type Props = {
    suggestionsList: ProductType[];
    categorySlugMap?: Record<number, string>;
};

const AutoSuggestInput: React.FC<Props> = ({
    suggestionsList,
    categorySlugMap,
}) => {
    const router = useRouter();
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState<ProductType | null>(null);

    const fuse = useMemo(
        () =>
            new Fuse(suggestionsList, {
                keys: ['title'],
                threshold: 0.4,
            }),
        [suggestionsList],
    );

    const filteredSuggestions =
        query === ''
            ? suggestionsList
            : fuse.search(query).map(result => result.item);

    return (
        <Combobox<ProductType>
            onChange={product => {
                // Ici, product est bien typé comme ProductType
                setSelected(product);
                const categorySlug =
                    product.productCategories?.slug ??
                    (categorySlugMap
                        ? categorySlugMap[product.productCategoryId!]
                        : '');
                router.push(`/location/${categorySlug}/${product.slug}`);
            }}
            value={selected}
        >
            <div className="relative w-full max-w-md">
                <Combobox.Input
                    className="w-full rounded-md border-2 border-secondary focus:border-secondary focus:ring-1 focus:ring-secondary focus:outline-none p-2 text-black"
                    displayValue={(product: ProductType) =>
                        product?.title || ''
                    }
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Nom du produit recherché"
                />
                <Combobox.Options className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded-md bg-white border border-gray-300 shadow-lg">
                    {filteredSuggestions.length === 0 && query !== '' ? (
                        <div className="px-4 py-2 text-gray-500">
                            Aucun résultat
                        </div>
                    ) : (
                        filteredSuggestions.map(product => (
                            <Combobox.Option
                                className={({ active }) =>
                                    `cursor-pointer px-4 py-2 ${
                                        active
                                            ? 'bg-gray-100 text-black'
                                            : 'text-gray-800'
                                    }`
                                }
                                key={product.id}
                                value={product}
                            >
                                {product.title}
                            </Combobox.Option>
                        ))
                    )}
                </Combobox.Options>
            </div>
        </Combobox>
    );
};

export default AutoSuggestInput;
